import { useEffect, useRef, useCallback } from 'react';
import { useMachineStore } from '@/store/store';
import {
  ConnectionState,
  DEMO_MODE,
  WS_URL,
  type NackPayload,
  type IncomingMessage,
} from '@/types/machine.types';

const INITIAL_RECONNECT_DELAY = 1000;
const MAX_RECONNECT_DELAY = 16000;
const BACKOFF_MULTIPLIER = 2;

/**
 * Max gap between STATUS frames before the connection is considered stale.
 * Backend broadcasts at 20Hz (every 50ms), so 2s ≈ 40 missed frames — a clear
 * sign the socket is open but the backend stopped sending (zombie connection).
 */
const STALE_TIMEOUT_MS = 2000;

/** Close code used when we drop a stale (but still "open") socket ourselves. */
const STALE_CLOSE_CODE = 4000;

/**
 * Connects to the backend WebSocket and writes incoming data to Zustand store.
 * Handles STATUS payloads (machine state) and ERROR payloads (alarm system).
 * Auto-reconnects with exponential backoff (1s → 2s → 4s → 8s → 16s max).
 */
export function useMachineConnect(): void {
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectDelayRef = useRef(INITIAL_RECONNECT_DELAY);
  const staleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isMountedRef = useRef(true);
  // Holds the latest scheduleReconnect so `connect` can trigger a reconnect
  // without a declaration cycle between the two callbacks.
  const scheduleReconnectRef = useRef<() => void>(() => undefined);

  const setConnectionState = useMachineStore((s) => s.setConnectionState);
  const updateMachineStatus = useMachineStore((s) => s.updateMachineStatus);
  const addError = useMachineStore((s) => s.addError);
  const setWebSocket = useMachineStore((s) => s.setWebSocket);

  const clearStaleTimer = useCallback(() => {
    if (staleTimerRef.current !== null) {
      clearTimeout(staleTimerRef.current);
      staleTimerRef.current = null;
    }
  }, []);

  /**
   * (Re)arms the staleness watchdog. Called on every STATUS frame and on open.
   * If no STATUS arrives within STALE_TIMEOUT_MS, the socket is "open" but the
   * backend stopped sending — close it so the normal reconnect path kicks in.
   */
  const resetStaleTimer = useCallback(() => {
    clearStaleTimer();
    staleTimerRef.current = setTimeout(() => {
      const ws = wsRef.current;
      if (ws && ws.readyState === WebSocket.OPEN) {
        console.warn(`⚠️ No STATUS for ${STALE_TIMEOUT_MS.toString()}ms — dropping stale socket`);
        ws.close(STALE_CLOSE_CODE, 'Stale connection');
      }
    }, STALE_TIMEOUT_MS);
  }, [clearStaleTimer]);

  const handleMessage = useCallback(
    (event: MessageEvent<string>) => {
      try {
        const data: IncomingMessage = JSON.parse(event.data) as IncomingMessage;

        switch (data.type) {
          case 'STATUS':
            resetStaleTimer();
            updateMachineStatus(data);
            break;
          case 'ERROR':
            addError(data);
            break;
          case 'ACK':
            if (import.meta.env.DEV) {
              console.debug('✅ ACK:', data.command);
            }
            break;
          case 'NACK': {
            const nack: NackPayload = data;
            console.warn(`⛔ NACK (${nack.command}): ${nack.reason}`);
            // Surface as a non-critical info toast so the operator sees the rejection.
            addError({
              type: 'ERROR',
              kind: 'OPERATOR_TEXT',
              message: `Command rejected (${nack.command}): ${nack.reason}`,
              timestamp: nack.timestamp,
            });
            break;
          }
          default:
            if (import.meta.env.DEV) {
              console.debug('📨 Unknown message type:', data);
            }
        }
      } catch (parseError) {
        console.error('❌ WebSocket message parse error:', parseError);
      }
    },
    [updateMachineStatus, addError, resetStaleTimer],
  );

  const connect = useCallback(() => {
    if (!isMountedRef.current) return;

    if (wsRef.current) {
      wsRef.current.onopen = null;
      wsRef.current.onclose = null;
      wsRef.current.onerror = null;
      wsRef.current.onmessage = null;
      if (
        wsRef.current.readyState === WebSocket.OPEN ||
        wsRef.current.readyState === WebSocket.CONNECTING
      ) {
        wsRef.current.close();
      }
    }

    setConnectionState(ConnectionState.CONNECTING);

    const ws = new WebSocket(WS_URL);
    wsRef.current = ws;
    setWebSocket(ws);

    ws.onopen = () => {
      if (!isMountedRef.current) return;
      console.log('✅ WebSocket connected');
      setConnectionState(ConnectionState.CONNECTED);
      reconnectDelayRef.current = INITIAL_RECONNECT_DELAY;
      resetStaleTimer();
    };

    ws.onclose = (event) => {
      if (!isMountedRef.current) return;
      clearStaleTimer();
      console.warn(
        `❌ WebSocket closed (code: ${event.code.toString()}) — ` +
          `reconnecting in ${reconnectDelayRef.current.toString()}ms...`,
      );
      setConnectionState(ConnectionState.DISCONNECTED);
      setWebSocket(null);
      scheduleReconnectRef.current();
    };

    ws.onerror = (error) => {
      console.error('❌ WebSocket error:', error);
    };

    ws.onmessage = handleMessage;
  }, [setConnectionState, setWebSocket, handleMessage, resetStaleTimer, clearStaleTimer]);

  const scheduleReconnect = useCallback(() => {
    if (!isMountedRef.current) return;

    if (reconnectTimerRef.current !== null) {
      clearTimeout(reconnectTimerRef.current);
    }

    const delay = reconnectDelayRef.current;

    reconnectTimerRef.current = setTimeout(() => {
      if (isMountedRef.current) {
        connect();
      }
    }, delay);

    reconnectDelayRef.current = Math.min(delay * BACKOFF_MULTIPLIER, MAX_RECONNECT_DELAY);
  }, [connect]);

  useEffect(() => {
    scheduleReconnectRef.current = scheduleReconnect;
  }, [scheduleReconnect]);

  useEffect(() => {
    // Web/demo build: never open the socket (there is no backend). Seed a
    // connected, fully-homed, non-E-stop state so the SafetyOverlay clears and
    // the static UI is visible. No reconnect loop, no localhost probing.
    if (DEMO_MODE) {
      useMachineStore.setState({
        connectionState: ConnectionState.CONNECTED,
        machineMode: 'SIMULATION',
        enabled: true,
        homed: new Array(9).fill(1) as number[],
      });
      return;
    }

    isMountedRef.current = true;
    connect();

    return () => {
      isMountedRef.current = false;

      clearStaleTimer();

      if (reconnectTimerRef.current !== null) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }

      if (wsRef.current) {
        wsRef.current.onopen = null;
        wsRef.current.onclose = null;
        wsRef.current.onerror = null;
        wsRef.current.onmessage = null;

        if (
          wsRef.current.readyState === WebSocket.OPEN ||
          wsRef.current.readyState === WebSocket.CONNECTING
        ) {
          wsRef.current.close(1000, 'Component unmounted');
        }

        wsRef.current = null;
        setWebSocket(null);
      }

      setConnectionState(ConnectionState.DISCONNECTED);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
